import { Badge, Button, Eyebrow } from "@pej/kore/components";
import { LogOut, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { fetchSignOutUrl, signOutEnabled } from "@/api/accounts";
import { useIdentity } from "@/api/me";

interface IdentityCardProps {
    platformStaff: boolean;
}

export function IdentityCard({ platformStaff }: IdentityCardProps) {
    const identity = useIdentity();
    const [signingOut, setSigningOut] = useState(false);
    const canSignOut = signOutEnabled();
    const user = identity.data?.user;

    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        const href = await fetchSignOutUrl();
        if (href) {
            // Top-level JS navigation (not <a href>): Arc browser's
            // "Little Arc" and similar popup heuristics apply only to
            // cross-origin anchor-element clicks. Setting window.location
            // bypasses them, so SSO sign-out navigates the main window
            // instead of opening a floating mini window we'd then sign
            // back into.
            window.location.assign(href);
        } else {
            setSigningOut(false);
        }
    };

    if (!user) return null;

    return (
        <div className="mb-7 flex items-center gap-4 rounded-xl border border-border bg-card px-5 py-4">
            <div className="min-w-0 flex-1">
                <Eyebrow as="p" className="mb-0.5">
                    Signed in as
                </Eyebrow>
                <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-base font-semibold text-foreground">{user.name}</span>
                    {platformStaff && (
                        <Badge tone="accent" className="text-[10px] font-bold uppercase tracking-wider">
                            <ShieldCheck className="size-3" />
                            Platform staff
                        </Badge>
                    )}
                </div>
                {user.email && <div className="mt-0.5 text-xs text-muted-foreground">{user.email}</div>}
            </div>
            {canSignOut && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        void handleSignOut();
                    }}
                    disabled={signingOut}
                >
                    <LogOut className="size-3.5" />
                    {signingOut ? "Signing out…" : "Sign out"}
                </Button>
            )}
        </div>
    );
}
